<template>
  <b-modal
    id="representation-modal"
    ref="representationModal"
    modal-class="custom-modal info"
    size="lg"
    centered
    hide-footer
    @show="onModalShow"
  >
    <template #modal-title>
      <h4 class="d-flex align-items-center mb-0">
        <fa-icon :icon="['fas', 'exclamation-circle']" class="icon-blue mr-2" />
        {{ $t("edit_current_payees_and_societies") | capitalize }}
      </h4>
    </template>

    <t-table
      id="representation-modal-table"
      :columns="cols"
      :options="tableOptions"
      :local-data="items"
    >
      <template v-slot:name="{ row }">
        {{ row.name }}
      </template>
      <template v-slot:societyName="{ row }">
        {{ row.societyName }}
      </template>
      <template v-slot:split="{ row }">
        {{ row.split }}%
      </template>
    </t-table>

    <div v-if="!disabled">
      <h5 class="mb-3">{{ $t("new_payees_and_societies") | capitalize }}</h5>
      <b-row align-v="end">
        <b-col cols="5">
          <b-form-group :label="$t('payee') | capitalize" class="mb-0">
            <multi-select
              v-model="newEntry.payee"
              :options="payeeOptions"
              :multiple="false"
              :close-on-select="true"
              :internal-search="false"
              :disabled="payeeFieldDisabled"
              label="name"
              track-by="salesforceId"
              :placeholder="$t('payee') | capitalize"
              :loading="payeeLoading"
              @search-change="fetchPayees"
              @select="onPayeeSelect"
            >
              <template v-slot:noOptions>{{ $t("type_to_search") | capitalize }}</template>
            </multi-select>
          </b-form-group>
        </b-col>
        <b-col cols="3">
          <b-form-group :label="$t('current_society') | capitalize" class="mb-0">
            <multi-select
              v-model="newEntry.society"
              :options="societyOptions"
              :multiple="false"
              :close-on-select="true"
              :disabled="societyFieldDisabled"
              label="name"
              track-by="value"
              :placeholder="$t('current_society') | capitalize"
              @select="onSocietySelect"
            />
          </b-form-group>
        </b-col>
        <b-col cols="2">
          <b-form-group :label="$t('split') | capitalize" class="mb-0">
            <b-form-input
              v-model.number="newEntry.split"
              type="number"
              min="0"
              max="100"
              :placeholder="$t('split') | capitalize"
            />
          </b-form-group>
        </b-col>
        <b-col cols="2" class="d-flex align-items-end justify-content-end">
          <b-button
            class="btn-outline-red"
            :disabled="!canAddEntry"
            @click="addEntry"
          >
            {{ $t("add") | capitalize }}
          </b-button>
        </b-col>
      </b-row>
    </div>

    <t-table
      v-if="pendingItems.length"
      id="representation-pending-table"
      :columns="pendingCols"
      :options="pendingTableOptions"
      :local-data="pendingItems"
    >
      <template v-slot:name="{ row }">
        {{ row.name }}
      </template>
      <template v-slot:societyName="{ row }">
        {{ row.societyName }}
      </template>
      <template v-slot:split="{ row }">
        {{ row.split }}%
      </template>
      <template v-slot:actions="{ row }">
        <fa-icon
          role="button"
          size="sm"
          :icon="['fa', 'trash']"
          class="clickable text-danger"
          @click="removeEntry(row)"
        />
      </template>
    </t-table>

    <b-row class="modal-footer float-right mt-4" align-v="center">
      <span v-if="pendingItems.length > 0 && totalPendingSplit !== 100" class="text-danger mr-3">
        {{ $t("split_must_total_100") }}
      </span>
      <span v-else-if="pendingItems.length > 0 && !pendingItemsDifferent" class="text-danger mr-3">
        {{ $t("pending_representation_is_same") | capitalize }}
      </span>
      <b-button class="btn-outline-dark mr-2" @click="$bvModal.hide('representation-modal')">
        {{ $t("cancel") | capitalize }}
      </b-button>
      <b-button
        class="btn-outline-red"
        :disabled="!canConfirm || disabled"
        @click="confirm"
      >
        {{ $t("confirm") | capitalize }}
      </b-button>
    </b-row>
  </b-modal>
</template>

<script>
import { capitalize, debounce } from "lodash"
import { mapState } from "vuex"

export default {
  name: "RepresentationModal",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    college: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      currentRow: null,
      initialPendingItems: [],
      newEntry: { payee: null, society: null, split: null },
      pendingItems: [],
      payeeOptions: [],
      payeeLoading: false,
      payeeFieldDisabled: false,
      societyFieldDisabled: false,
      cols: ["name", "societyName", "split"],
      tableOptions: {
        sortable: [],
        headings: {
          name: capitalize(this.$t("name")),
          societyName: capitalize(this.$t("society")),
          split: capitalize(this.$t("split")),
        },
      },
      pendingCols: ["name", "societyName", "split", "actions"],
      pendingTableOptions: {
        sortable: [],
        headings: {
          name: capitalize(this.$t("name")),
          societyName: capitalize(this.$t("society")),
          split: capitalize(this.$t("split")),
          actions: "",
        },
        columnsClasses: {
          actions: "width-nano",
        },
      },
    }
  },
  computed: {
    ...mapState("consts", ["memberCollectives"]),
    societyOptions () {
      return this.memberCollectives
        .filter(c => {
          if (this.college === "performer") return c.isPerformers
          if (this.college === "maker") return c.isMakers
          return true
        })
        .map(c => ({ value: c.uuid, name: c.name }))
    },
    totalPendingSplit () {
      return this.pendingItems.reduce((sum, item) => sum + Number(item.split), 0)
    },
    canAddEntry () {
      const newSplit = Number(this.newEntry.split)
      return (
        this.newEntry.payee &&
        this.newEntry.society &&
        this.newEntry.split !== null &&
        this.newEntry.split !== "" &&
        newSplit > 0 &&
        newSplit <= 100 &&
        this.totalPendingSplit + newSplit <= 100
      )
    },
    pendingItemsDifferent () {
      let oldItems = new Set(this.items.map(it => JSON.stringify([it.id, it.societyName, it.split])))
      return !(
        this.pendingItems.every(it => oldItems.has(JSON.stringify([it.payeeId, it.societyName, it.split.toString()])))
        && this.pendingItems.length === oldItems.size
      )
    },
    canConfirm () {
      return this.pendingItems.length > 0 && this.totalPendingSplit === 100 && this.pendingItemsDifferent
    },
  },
  created () {
    this.fetchPayees = debounce(this._fetchPayees, 500)
  },
  methods: {
    show (row, pendingItems = []) {
      this.currentRow = row
      this.initialPendingItems = pendingItems ? [...pendingItems] : []
      this.$refs.representationModal.show()
    },
    onModalShow () {
      this.pendingItems = [...this.initialPendingItems]
      this.newEntry = { payee: null, society: null, split: null }
      this.payeeOptions = []
      this.payeeFieldDisabled = false
      this.societyFieldDisabled = false
    },
    async _fetchPayees (search = "") {
      if (!search && this.newEntry.payee) return
      if (!search) return
      this.payeeLoading = true
      try {
        const { data } = await this.$api.rightsholders.payeesList(search ? { search } : null)
        this.payeeOptions = data
      } finally {
        this.payeeLoading = false
      }
    },
    async onSocietySelect (society) {
      const autoMatchSocieties = ["ARTISTI", "SOPROQ", "ACTRA RACS"]
      if (autoMatchSocieties.includes(society.name)) {
        await this._fetchPayees(society.name)
        this.newEntry.payee = this.payeeOptions.find(p => p.name === society.name) || null
        this.payeeFieldDisabled = true
      } else {
        this.payeeFieldDisabled = false
      }
      // Don't disable society field when selecting society (user initiated the action)
      this.societyFieldDisabled = false
    },
    onPayeeSelect (payee) {
      const autoMatchPayees = ["ARTISTI", "SOPROQ", "ACTRA RACS"]
      
      if (autoMatchPayees.includes(payee.name)) {
        // For special payees, try exact name match with society
        this.newEntry.society = this.societyOptions.find(s => s.name === payee.name) || null
        this.societyFieldDisabled = true
      } else {
        // For regular payees, use memberCollective from backend
        this.newEntry.society = this.societyOptions.find(s => s.name === payee.memberCollective) || null
        this.societyFieldDisabled = false
      }
    },
    addEntry () {
      this.pendingItems.push({
        name: this.newEntry.payee.name,
        societyName: this.newEntry.society.name,
        split: this.newEntry.split,
        payeeId: this.newEntry.payee.salesforceId,
        societyId: this.newEntry.society.value,
      })
      this.newEntry = { payee: null, society: null, split: null }
      this.payeeFieldDisabled = false
      this.societyFieldDisabled = false
    },
    removeEntry (row) {
      const idx = this.pendingItems.indexOf(row)
      if (idx >= 0) this.pendingItems.splice(idx, 1)
    },
    confirm () {
      this.$emit("confirm", { row: this.currentRow, items: this.pendingItems })
      this.$bvModal.hide("representation-modal")
    },
  },
}
</script>
